import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { DataRequestsState } from '@/types/dataRequestsState';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { PageList } from '@/types/list';
import type { PaginationNormalizedResponse } from '@/types/pagination';
import { ProducerEntityType } from '@/domains/producers/types/ProducerEntityType';
import type { EntityDetailsModel } from '@/features/entityDetails/types/entityDetailsData';
import type { GetContactListResponse } from '@/features/entityDetails/types/responses/getContactListResponse';
import type { GetEntityAddressHistoryPayload } from './types/GetEntityAddressHistoryPayload';
import type { RefreshContactsListPayload } from './types/RefreshContactsListPayload';
import { AddressHoldersEnum } from '@/enums/AddressHoldersEnum';
import PaginationService from '@/services/PaginationService/PaginationService';

export type EntityDetailsSliceState = {
  dataRequestsStates: {
    entityDetails: DataRequestsState;
    contactList: DataRequestsState;
    addressHistory: {
      isLoading: boolean;
    };
  };

  entityDetails: EntityDetailsModel;

  contactList: EntityDetailsModel[];
  contactDetailsListPaginationRequest: PageList;
  contactDetailsListPaginationResponse: PaginationNormalizedResponse | null;
  contactAddressHistories: Record<number, NormalizedV2Response[]>;
};

export type GetBeneficiariesListPayload = {
  claims: string[];
};

const initialState: EntityDetailsSliceState = {
  contactList: [],
  contactDetailsListPaginationRequest: {
    page: 1,
    perPage: 50,
  },
  contactDetailsListPaginationResponse: null,
  contactAddressHistories: {},

  entityDetails: {
    entityDetailsData: undefined,
    entityAddressList: [],
    personOrBusinessData: undefined,
    contactInformationData: undefined,
    entityAddressHistory: null,
  },

  dataRequestsStates: {
    entityDetails: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    contactList: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    addressHistory: {
      isLoading: false,
    },
  },
};

const entityDetailsSlice = createSlice({
  name: 'entityDetails',
  initialState,
  reducers: {
    // CONTACTS
    getContactList() {},
    startLoadingContactList(state) {
      state.dataRequestsStates.contactList = {
        isLoading: true,
        isFetching: !state.dataRequestsStates.contactList,
        isError: false,
        isSuccess: false,
      };
    },
    getContactListSuccess(state, { payload }: PayloadAction<GetContactListResponse>) {
      state.dataRequestsStates.contactList = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };

      const normalizedResponse = PaginationService.normalizePaginatedListResponse(payload);

      state.contactDetailsListPaginationResponse = normalizedResponse.pagination;

      if (normalizedResponse.pagination.page === 1) {
        state.contactList = normalizedResponse.dataList;
      } else {
        state.contactList = state.contactList.concat(normalizedResponse.dataList);
      }
    },
    refreshContactList(_, _action: PayloadAction<RefreshContactsListPayload | undefined>) {},
    refreshContactListSuccess(state, { payload }: PayloadAction<EntityDetailsModel[]>) {
      state.contactList = payload;

      state.dataRequestsStates.contactList = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    incrementContactListPage(state) {
      state.contactDetailsListPaginationRequest.page++;
    },
    resetContactList: (state) => {
      state.contactDetailsListPaginationRequest.page = 1;
      state.contactList = [];
    },
    // ENTITIES
    clearEntityDetails(state) {
      state.entityDetails = initialState.entityDetails;
    },
    getBeneficiaryDetails: (
      _state,
      _action: PayloadAction<{
        beneficiaryId: string;
      }>,
    ) => {},
    getInsuredDetails: (
      _state,
      _action: PayloadAction<{
        insuredId: string;
      }>,
    ) => {},
    getProducerDetails: (
      _state,
      _action: PayloadAction<{
        id: string;
        producerEntityType: ProducerEntityType;
      }>,
    ) => {},
    startLoadingEntityDetails: (state) => {
      state.dataRequestsStates.entityDetails = {
        isLoading: true,
        isFetching:
          !state.dataRequestsStates.entityDetails.isError && !state.dataRequestsStates.entityDetails.isSuccess,
        isError: false,
        isSuccess: false,
      };
    },
    getEntityDetailsSuccess: (state, action: PayloadAction<EntityDetailsModel>) => {
      state.entityDetails = action.payload;
      state.dataRequestsStates.entityDetails = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getEntityDetailsError: (state) => {
      state.dataRequestsStates.entityDetails = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    getEntityAddressHistory: (state, _action: PayloadAction<GetEntityAddressHistoryPayload>) => {
      state.dataRequestsStates.addressHistory = {
        isLoading: true,
      };
    },
    getEntityAddressHistorySuccess: (
      state,
      { payload: { data, entityTypeId, entity } }: PayloadAction<GetEntityAddressHistoryPayload & { data: any }>,
    ) => {
      if (entity === AddressHoldersEnum.Contact) {
        state.contactAddressHistories[entityTypeId] = data ?? [];
      } else {
        state.entityDetails.entityAddressHistory = data ?? [];
      }

      state.dataRequestsStates.addressHistory = {
        isLoading: false,
      };
    },
    getEntityAddressHistoryError: (state) => {
      state.dataRequestsStates.addressHistory = {
        isLoading: false,
      };
    },

    deleteEntityPaymentInformation: (_state, _action: PayloadAction<{ paymentMethodId: string | number }>) => {},
  },
});

export const {
  getContactList,
  startLoadingContactList,
  getContactListSuccess,
  refreshContactList,
  refreshContactListSuccess,
  incrementContactListPage,
  resetContactList,
  clearEntityDetails,
  getBeneficiaryDetails,
  getInsuredDetails,
  getProducerDetails,
  startLoadingEntityDetails,
  getEntityDetailsSuccess,
  getEntityDetailsError,
  getEntityAddressHistory,
  getEntityAddressHistorySuccess,
  getEntityAddressHistoryError,
  deleteEntityPaymentInformation,
} = entityDetailsSlice.actions;

export const entityDetailsReducer = entityDetailsSlice.reducer;
export const entityDetailsReducerPath = entityDetailsSlice.reducerPath;
